import { IOtherAttributesMathFormula } from "@schemas/otherAttributesSchema";
import { Alert, Anchor, Badge, Box, Button, Group, Stack, Text } from "@mantine/core";
import React, { useEffect, useMemo } from "react";
import {
  selectMathFormulaConfigDraft,
  selectMathFormulaConfigValidationError,
  selectMathFormulaSupportedAttributes,
} from "../../../../app/routes/issuesApp/issueSelectors";
import {
  resetMathFormulaConfigInRedux,
  setMathFormulaConfigDraftInRedux,
} from "../../../../app/routes/issuesApp/issueSlice";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { McInputNew, McTextAreaNew, McTitle } from "../../../index";
import { buildMathFormula, MATH_FORMULA_EXAMPLES } from "./mathFormulaConfigUtils";

const MathFormulaConfig: React.FC<{
  initialMathFormula: IOtherAttributesMathFormula;
  currentAttributeId?: string;
  onMathFormulaUpdate: (mathFormula: IOtherAttributesMathFormula) => void;
}> = ({ initialMathFormula, currentAttributeId, onMathFormulaUpdate }) => {
  const dispatch = useAppDispatch();
  const supportedAttributes = useAppSelector((state) =>
    selectMathFormulaSupportedAttributes(state, currentAttributeId),
  );
  const localMathFormula = useAppSelector(selectMathFormulaConfigDraft);
  const validationError = useAppSelector((state) => selectMathFormulaConfigValidationError(state, currentAttributeId));
  const variables = useMemo(() => supportedAttributes.map((attribute) => attribute.key), [supportedAttributes]);

  useEffect(() => {
    dispatch(
      setMathFormulaConfigDraftInRedux({
        expression: initialMathFormula?.expression ?? "",
        variables: initialMathFormula?.variables ?? [],
      }),
    );

    return () => {
      dispatch(resetMathFormulaConfigInRedux());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!localMathFormula) {
      return;
    }

    if (localMathFormula.variables.join("|") === variables.join("|")) {
      return;
    }

    dispatch(
      setMathFormulaConfigDraftInRedux(
        buildMathFormula({
          expression: localMathFormula.expression ?? "",
          variables,
        }),
      ),
    );
  }, [dispatch, localMathFormula, variables]);

  useEffect(() => {
    if (!localMathFormula) {
      return;
    }

    if (!validationError) {
      onMathFormulaUpdate(localMathFormula);
    }
  }, [localMathFormula, onMathFormulaUpdate, validationError]);

  const updateExpression = (expression: string) => {
    dispatch(
      setMathFormulaConfigDraftInRedux(
        buildMathFormula({
          expression,
          variables,
        }),
      ),
    );
  };

  if (!localMathFormula) {
    return null;
  }

  return (
    <Box
      mt="md"
      p="md"
      style={{
        border: "1px solid var(--mantine-color-gray-3)",
        borderRadius: "8px",
        background: "var(--mantine-color-gray-0)",
      }}
    >
      <Stack gap="md">
        <Alert color="yellow" variant="light" title="Beta">
          Denne funksjonen er i beta. Bruk enkle uttrykk med attributt-IDer som peker på tallfelt eller listeverdier som
          kan tolkes som tall, for eksempel <code>A</code>, <code>B</code> og <code>C</code>. Enkelte begrensninger og
          hvordan formler vises i appen kan bli justert.
        </Alert>

        <McTitle title="Matematisk formel">
          <McTextAreaNew
            value={localMathFormula.expression ?? ""}
            onChange={(e) => updateExpression(e.target.value)}
            placeholder="f.eks. (A - B) / A * 100"
          />
        </McTitle>

        {validationError && <Alert color="red">{validationError}</Alert>}

        <Text size="sm" c="dimmed">
          Bruk attributt-IDer som <code>A</code>, <code>B</code> og <code>C</code> for tallfelt eller lister med
          numeriske verdier, operatorer som <code>+</code>, <code>-</code>, <code>*</code>, <code>/</code>, parenteser
          og enkle funksjoner som <code>round()</code>, <code>min()</code> og <code>max()</code>. Prosentverdier som{" "}
          <code>25%</code> og <code>25 %</code> støttes.
        </Text>
        <Anchor href="https://mathjs.org/docs/expressions/syntax.html" target="_blank" rel="noreferrer" size="sm">
          Se math.js-dokumentasjon for syntaks
        </Anchor>

        <Box>
          <Text size="sm" fw={600} mb="xs">
            Eksempler
          </Text>
          <Group gap="xs">
            {MATH_FORMULA_EXAMPLES.map((example) => (
              <Button key={example} size="xs" variant="light" color="gray" onClick={() => updateExpression(example)}>
                {example}
              </Button>
            ))}
          </Group>
        </Box>

        <Box>
          <Text size="sm" fw={600} mb="xs">
            Tilgjengelige felt
          </Text>
          {supportedAttributes.length === 0 ? (
            <Text size="sm" c="dimmed">
              Ingen andre støttede felt tilgjengelig ennå.
            </Text>
          ) : (
            <Group gap="xs">
              {supportedAttributes.map((attribute) => (
                <Badge key={attribute.key} variant="light" color="gray">
                  {attribute.key}: {attribute.label}
                  {attribute.typeOfValue === "DROPDOWN" ? " (liste)" : ""}
                </Badge>
              ))}
            </Group>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default MathFormulaConfig;
